import React from 'react';
import { faComments } from '@fortawesome/free-solid-svg-icons';
import { getInputFormat } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import {
    onFieldChange,
    toasty,
    onReactSelectChanged,
    Input,
    Textarea,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import {
    ContactActivity,
    ActivityTypeOptions,
    KnownActivityTypes,
} from './ContactActivity';

const isActivityNew = (contactActivity) => !contactActivity.id;

const isActivityNote = (contactActivity) => Boolean(
    contactActivity && contactActivity.activityType === KnownActivityTypes.Note,
);

const isActivityTask = (contactActivity) => Boolean(
    contactActivity && contactActivity.activityType === KnownActivityTypes.Task,
);

const canBeConfirmed = (contactActivity) => Boolean(
    !isActivityNew(contactActivity)
    && isActivityTask(contactActivity)
    && contactActivity.activityTaskStatusId != 3,
);

const getActivityTypeDescription = (contactActivity) => {
    let activityTypeDescription = 'Contact Activity';

    if (isActivityNote(contactActivity)) {
        activityTypeDescription = 'Note';
    }

    if (isActivityTask(contactActivity)) {
        activityTypeDescription = 'Task';
    }

    return activityTypeDescription;
};

const getSecondarySaveButtonText = (contactActivity) => {
    if (!canBeConfirmed(contactActivity)) {
        return null;
    }

    return 'Save and Complete';
};

export default class ContactActivityForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            contactActivity: null,
            show: false,
            activityTaskStatuses: props.activityTaskStatuses || [],
            activityTaskTypes: props.activityTaskTypes || [],
            taskInteractionTypes: props.activityTaskTypes || [],
            activityTypes: ActivityTypeOptions,
            contactName: '',
            readOnly: false,
            removeUnavailable: true,
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onSubmitAndConfirm = this.onSubmitAndConfirm.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);

        this.onSelectChange = this.onSelectChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onChange = onFieldChange;

    onClose = (response) => {
        this.resetForm();
        this.setState({ show: false });
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onDelete = async () => {
        this.setState({ readOnly: true });

        const { contactActivity } = this.state;

        const response = await httpClient
            .delete(ApiRoutes.contactActivity.delete(contactActivity.id))
            .catch(this.handleSaveError);

        if (response) {
            this.setState({ show: false });
            this.props.onDeleteCallback(response);
        }

        this.setState({ readOnly: false });
    };

    onSelectChange = onReactSelectChanged;

    onSubmit = async () => {
        this.setState({ readOnly: true });

        // Clear any fluent api errors
        const { contactActivity } = this.state;

        let savedSuccessfully = false;

        const activityType = isActivityNote(contactActivity) ? 'Note' : 'Task';

        if (contactActivity.id && contactActivity.id > 0) {
            savedSuccessfully = await this.updateActivity(contactActivity);

            if (savedSuccessfully) {
                toasty.success(
                    `${activityType} Updated`,
                    `${activityType} Successfully Updated`,
                );
            }
        } else {
            savedSuccessfully = await this.createActivity(contactActivity);

            if (savedSuccessfully) {
                toasty.success(
                    `${activityType} Created`,
                    `${activityType} Successfully Created`,
                );
            }
        }

        this.setState({ readOnly: false });

        if (!savedSuccessfully) {
            return;
        }

        this.clearErrors();
        this.setState({ contactName: '', show: false });
        this.context.setFormOpened(false);
        this.props.onSaveCallback();
        this.resetForm();
    };

    onSubmitAndConfirm = async (e) => {
        this.setState({ readOnly: true });

        const { contactActivity } = this.state;

        contactActivity.activityTaskStatusId = 3;

        this.setState({ contactActivity });

        await this.onSubmit(e);
    };



    clearErrors = () => this.setState({ errors: {} });

    createActivity = async (contactActivity) => {
        const createResponse = await httpClient
            .post(ApiRoutes.contactActivity.create(), contactActivity)
            .catch(this.handleSaveError);

        return Boolean(createResponse);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = async (id) => {
        this.setState({ loading: true });

        const [contactActivity] = await Promise.all([
            httpClient.js(ApiRoutes.contactActivity.byId(id)),
        ]);

        if (!contactActivity) {
            toasty.error(
                'Contact Activity',
                'We failed to find that activity. Please refresh and try again.',
            );

            this.setState({ loading: false });
            return;
        }

        contactActivity.taskDueDate = getInputFormat(
            contactActivity.taskDueDate,
        );

        contactActivity.noteDate = getInputFormat(
            contactActivity.noteDate,
        );

        let contactName = '[UNKNOWN]';
        const { locationContact } = contactActivity;

        if (locationContact) {
            contactName = `${locationContact.lastName}, ${locationContact.firstName}`;
        }
        this.context.setFormOpened(true);
        this.setState({
            show: true,
            loading: false,
            contactName,
            contactActivity,
        });
    };

    openNew = (locationContactId, contactName) => {
        const contactActivity = new ContactActivity();

        contactActivity.locationContactId = locationContactId;

        // notes should default to today's date
        contactActivity.noteDate = getInputFormat(
            new Date().toLocaleDateString(),
        );

        // tasks should default to pending
        contactActivity.activityTaskStatusId = 1;

        this.setState({ contactActivity, contactName, show: true });
    };

    populateState = async () => {
        let { activityTaskStatuses, activityTaskTypes, taskInteractionTypes } = this.state;

        const removeUnavailable = !this.context.permissions.find(
            (p) => p.permission === 'contactactivity' && p.action === 'delete',
        );

        if (
            !activityTaskStatuses
            || !activityTaskStatuses.length
            || !activityTaskTypes
            || !activityTaskTypes.length
            || !taskInteractionTypes
            || !taskInteractionTypes.length
        ) {
            [activityTaskTypes, activityTaskStatuses, taskInteractionTypes] = await Promise.all([
                httpClient.js(ApiRoutes.typeAheads.activityTaskTypes()),
                httpClient.js(ApiRoutes.typeAheads.activityTaskStatuses()),
                httpClient.js(ApiRoutes.typeAheads.taskInteractionTypes()),
            ]);

            this.setState({
                activityTaskTypes,
                activityTaskStatuses,
                taskInteractionTypes,
                removeUnavailable,
            });
        }

        this.setState({ loading: false });
    };

    resetForm = () => this.setState({ formValidated: false });

    updateActivity = async (contactActivity) => {
        const updateResponse = await httpClient
            .put(
                ApiRoutes.contactActivity.update(contactActivity.id),
                contactActivity,
            )
            .catch(this.handleSaveError);

        return Boolean(updateResponse);
    };

    render() {
        const {
            contactActivity,
            formValidated,
            validationMessage,
            loading,
            show,
            activityTaskStatuses,
            activityTaskTypes,
            taskInteractionTypes,
            activityTypes,
            contactName,
            readOnly,
            removeUnavailable,
        } = this.state;

        if (!contactActivity || !contactActivity.locationContactId) {
            return '';
        }

        const isNew = isActivityNew(contactActivity);
        const isNote = isActivityNote(contactActivity);
        const isTask = isActivityTask(contactActivity);

        const titlePrefix = isNew ? 'Add' : 'Edit';
        const activityTypeDescription = getActivityTypeDescription(contactActivity);
        const nameAddendum = contactName ? ` for ${contactName}` : '';
        const secondarySaveButtonText = getSecondarySaveButtonText(contactActivity);

        return (
            <SlideForm
                loading={loading}
                show={show}
                id="ContactActivityForm"
                formIcon={faComments}
                formTitle={`${titlePrefix} ${activityTypeDescription}${nameAddendum}`}
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onSave={this.onSave}
                onDelete={this.onDelete}
                errors={this.state.errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                entityId={contactActivity.id}
                readOnly={readOnly}
                removeUnavailable={removeUnavailable}
                secondarySaveButtonText={secondarySaveButtonText}
                onSecondarySubmit={this.onSubmitAndConfirm}
            >
                {isNew && (
                    <ValidatedSelect
                        id="activityType"
                        name="contactActivity.activityType"
                        label="Type"
                        required
                        options={activityTypes}
                        value={
                            activityTypes.find(
                                (at) => at.value
                                    === contactActivity.activityType,
                            ) ?? ''
                        }
                        onChange={this.onSelectChange}
                        error="Please tell us which type of activity should be
                            created."
                    />
                )}

                {isNote && (
                    <>
                        <Input
                            label="Title"
                            id="title"
                            name="contactActivity.title"
                            value={contactActivity.title ?? ''}
                            onChange={this.onChange}
                            placeholder="Enter Name (maximum 80 characters)"
                            maxLength="80"
                            pattern="[^()/><\][\\\x22,;|]+"
                            error="Title is required and can contain hyphens and periods."
                            required
                        />
                        <Input
                            label="Note Date"
                            id="contactActivity.noteDate"
                            name="contactActivity.noteDate"
                            value={
                                contactActivity.noteDate
                                ?? new Date().toLocaleDateString()
                            }
                            onChange={this.onChange}
                            placeholder="Note Date"
                            type="date"
                            error="Note Date is required."
                            required
                        />
                        <Textarea
                            label="Notes"
                            id="notes"
                            name="contactActivity.notes"
                            value={contactActivity.notes ?? ''}
                            onChange={this.onChange}
                            placeholder="Enter notes here."
                            maxLength="500"
                            rows="5"
                        />
                    </>
                )}

                {isTask && (
                    <>
                        <Textarea
                            label="Description"
                            id="description"
                            name="contactActivity.description"
                            value={contactActivity.description ?? ''}
                            onChange={this.onChange}
                            placeholder="Enter Description (maximum 250 characters)"
                            maxLength="250"
                            pattern="[^()/><\][\\\x22,;|]+"
                            rows="3"
                            error="Description is required."
                            required
                        />
                        <Input
                            label="Task Due Date"
                            id="contactActivity.taskDueDate"
                            name="contactActivity.taskDueDate"
                            value={
                                contactActivity.taskDueDate
                                ?? new Date().toLocaleDateString()
                            }
                            onChange={this.onChange}
                            placeholder="Due Date"
                            type="date"
                            error="Due Date is required."
                            required
                        />

                        <ValidatedSelect
                            id="activityTaskStatusId"
                            name="contactActivity.activityTaskStatusId"
                            label="Status"
                            required
                            options={activityTaskStatuses}
                            value={
                                activityTaskStatuses.find(
                                    (ats) => ats.value
                                        === contactActivity.activityTaskStatusId,
                                ) ?? ''
                            }
                            onChange={this.onSelectChange}
                            error="Please tell us what the status of this task is."
                        />

                        <ValidatedSelect
                            id="activityTaskTypeId"
                            name="contactActivity.activityTaskTypeId"
                            label="Task Type"
                            required
                            options={activityTaskTypes}
                            value={
                                activityTaskTypes.find(
                                    (att) => att.value
                                        === contactActivity.activityTaskTypeId,
                                ) ?? ''
                            }
                            onChange={this.onSelectChange}
                            error="Please tell us what type of task is."
                        />

                        <ValidatedSelect
                            id="taskInteractionTypeId"
                            name="contactActivity.taskInteractionTypeId"
                            label="Interaction Type"
                            required
                            options={taskInteractionTypes}
                            value={
                                taskInteractionTypes.find(
                                    (tt) => tt.value
                                        === contactActivity.taskInteractionTypeId,
                                ) ?? ''
                            }
                            onChange={this.onSelectChange}
                            error="Please tell us what type of interaction the task requires."
                        />

                        <Textarea
                            label="Details"
                            id="details"
                            name="contactActivity.details"
                            value={contactActivity.details ?? ''}
                            onChange={this.onChange}
                            placeholder="Enter Details (maximum 500 characters)"
                            maxLength="500"
                            rows="3"
                        />
                    </>
                )}
            </SlideForm>
        );
    }
}
